/**
 * mini-ide-workspace
 *
 * Normalizes tool events into the chat Canvas mini IDE model.
 *
 * Primary responsibilities:
 * - Convert code/file tool requests and results into workspace files
 * - Merge running tool progress into the active workspace
 * - Provide one browser event name for opening the workspace in Canvas
 *
 * Dependencies:
 * - None
 */

export const MINI_IDE_WORKSPACE_EVENT = 'openMiniIdeWorkspaceInCanvas'

export type MiniIdeWorkspaceStatus = 'pending' | 'running' | 'completed' | 'error'
export type MiniIdeWorkspaceSource = 'code' | 'computer' | 'virtual-files'

export interface MiniIdeWorkspaceFile {
  id?: string
  path: string
  name: string
  content?: string | null
  contentPreview?: string | null
  language?: string | null
  mimeType?: string | null
  dataUrl?: string | null
  bytes?: number | null
  truncated?: boolean
  status?: MiniIdeWorkspaceStatus
}

export interface ActiveMiniIdeWorkspace {
  id: string
  title: string
  files: MiniIdeWorkspaceFile[]
  source: MiniIdeWorkspaceSource
  status: MiniIdeWorkspaceStatus
  mergeKey?: string | null
  output?: string | null
  previewUrl?: string | null
  selectedPath?: string | null
  toolName?: string | null
  updatedAt: number
}

type UnknownRecord = Record<string, unknown>

// --------------

function asRecord(value: unknown): UnknownRecord | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  return value as UnknownRecord
}

function parseRecord(value: unknown): UnknownRecord | null {
  const record = asRecord(value)
  if (record) return record

  if (typeof value !== 'string') return null

  try {
    return asRecord(JSON.parse(value) as unknown)
  } catch {
    return null
  }
}

function unwrapParameters(value: unknown): UnknownRecord | null {
  const record = parseRecord(value)
  if (!record) return null

  return asRecord(record.parameters) ?? record
}

function readString(value: unknown) {
  return typeof value === 'string' && value.trim() ? value : null
}

function readOptionalString(value: unknown) {
  return typeof value === 'string' ? value : null
}

function readNumber(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function readBoolean(value: unknown) {
  return typeof value === 'boolean' ? value : null
}

function basename(path: string) {
  return path.split('/').filter(Boolean).pop() ?? path
}

function inferLanguage(path: string, fallback?: string | null) {
  if (fallback) return fallback

  const extension = path.split('.').pop()?.toLowerCase()

  switch (extension) {
    case 'ts':
    case 'tsx':
      return 'typescript'
    case 'js':
    case 'jsx':
      return 'javascript'
    case 'py':
      return 'python'
    case 'json':
      return 'json'
    case 'css':
      return 'css'
    case 'html':
    case 'htm':
      return 'html'
    case 'md':
    case 'mdx':
      return 'markdown'
    case 'sh':
    case 'bash':
      return 'bash'
    default:
      return 'text'
  }
}

function codePathForLanguage(language: string | null) {
  const normalized = (language ?? '').toLowerCase()

  if (normalized.includes('python')) return 'script.py'
  if (normalized.includes('typescript')) return 'snippet.ts'
  if (normalized.includes('javascript') || normalized.includes('node')) return 'snippet.js'
  if (normalized.includes('html')) return 'index.html'
  if (normalized.includes('css')) return 'style.css'

  return 'snippet.txt'
}

function workspaceId(source: MiniIdeWorkspaceSource, toolName?: string | null, suffix?: string) {
  return [source, toolName ?? 'workspace', suffix ?? 'active']
    .filter(Boolean)
    .join(':')
}

function getNestedRecord(record: UnknownRecord | null, key: string) {
  return asRecord(record?.[key])
}

function getWorkspaceMergeKey(
  source: MiniIdeWorkspaceSource,
  record: UnknownRecord | null,
  toolName?: string | null
) {
  const sandbox = getNestedRecord(record, 'sandbox')
  const explicitKey =
    readString(record?.workspaceId) ??
    readString(record?.workspace_id) ??
    readString(record?.sandboxName) ??
    readString(record?.name) ??
    readString(sandbox?.name)

  if (explicitKey) return `${source}:${explicitKey}`
  if (source === 'computer') return 'computer:active'

  return `${source}:${toolName ?? 'workspace'}`
}

function uniqueFiles(files: MiniIdeWorkspaceFile[]) {
  const byPath = new Map<string, MiniIdeWorkspaceFile>()

  for (const file of files) {
    if (!file.path) continue
    const previous = byPath.get(file.path)
    byPath.set(file.path, previous ? { ...previous, ...file } : file)
  }

  return [...byPath.values()].sort((left, right) => left.path.localeCompare(right.path))
}

function readWorkspaceFile(raw: unknown): MiniIdeWorkspaceFile | null {
  const record = asRecord(raw)
  if (!record) return null

  const path = readString(record.path)
  if (!path) return null

  const name = readString(record.name) ?? basename(path)
  const language = inferLanguage(path, readString(record.language))
  const content = readOptionalString(record.content)
  const contentPreview =
    readOptionalString(record.contentPreview) ?? readOptionalString(record.preview)
  const dataUrl = readString(record.dataUrl)
  const mimeType = readString(record.mimeType) ?? readString(record.mime_type)
  const bytes = readNumber(record.bytes)
  const id = readString(record.id)
  const truncated = readBoolean(record.truncated)

  return {
    ...(id ? { id } : {}),
    path,
    name,
    language,
    ...(content != null ? { content } : {}),
    ...(contentPreview != null ? { contentPreview } : {}),
    ...(dataUrl ? { dataUrl } : {}),
    ...(mimeType ? { mimeType } : {}),
    ...(bytes != null ? { bytes } : {}),
    ...(truncated != null ? { truncated } : {})
  }
}

function readWorkspaceFiles(record: UnknownRecord | null) {
  if (!record) return []

  const files: MiniIdeWorkspaceFile[] = []

  if (Array.isArray(record.files)) {
    for (const file of record.files) {
      const normalized = readWorkspaceFile(file)
      if (normalized) files.push(normalized)
    }
  }

  const singleFile = readWorkspaceFile(record)
  if (singleFile) files.push(singleFile)

  return uniqueFiles(files)
}

function getOutput(record: UnknownRecord | null, fallback?: string | null) {
  if (!record) return fallback ?? null

  const command = readString(record.command) ?? readString(record.cmd)
  const stdout = readString(record.stdout)
  const stderr = readString(record.stderr)
  const error = readString(record.error)
  const message =
    readString(record.message) ??
    readString(asRecord(record.progress)?.message) ??
    fallback

  return [
    command ? `\x1b[36m$ ${command}\x1b[0m` : null,
    stdout,
    stderr ? colorizeTerminalDiagnostics(stderr) : null,
    error ? `\x1b[31m${error}\x1b[0m` : null,
    !stdout && !stderr && !error ? message : null
  ]
    .filter((item): item is string => Boolean(item))
    .join('\n')
}

const WARNING_DIAGNOSTIC_PATTERN = /\b(warn(?:ing)?|deprecated|deprecation)\b/i
const ERROR_DIAGNOSTIC_PATTERN =
  /\b(error|failed|failure|fatal|exception|traceback|uncaught)\b/i

function isWarningOnlyDiagnosticText(value: string | null | undefined) {
  const text = value?.trim()
  if (!text) return false

  return WARNING_DIAGNOSTIC_PATTERN.test(text) && !ERROR_DIAGNOSTIC_PATTERN.test(text)
}

function colorizeTerminalDiagnostics(value: string) {
  const color = isWarningOnlyDiagnosticText(value) ? '33' : '31'

  return `\x1b[${color}m${value}\x1b[0m`
}

function getPreviewUrl(record: UnknownRecord | null) {
  if (!record) return null

  return (
    readString(record.previewUrl) ??
    readString(record.takeoverUrl) ??
    readString(asRecord(record.preview)?.url)
  )
}

function getStatus(record: UnknownRecord | null, fallback: MiniIdeWorkspaceStatus) {
  if (!record) return fallback

  if (readBoolean(record.success) === false || readString(record.error)) {
    return 'error'
  }

  return fallback
}

export function isMiniIdeToolName(toolName: string) {
  return (
    toolName.startsWith('sandbox__runCode') ||
    toolName.startsWith('sandbox__runCommand') ||
    toolName.startsWith('sandbox__writeFiles') ||
    toolName.startsWith('sandbox__startPreview') ||
    /VirtualFilesystem__losi__fs_(write_file|patch_file|list_files)/i.test(toolName) ||
    (toolName.includes('VirtualFilesystem') &&
      (toolName.includes('fs_write') ||
        toolName.includes('fs_patch') ||
        toolName.includes('fs_list')))
  )
}

export function normalizeMiniIdeWorkspacePayload(
  payload: unknown
): ActiveMiniIdeWorkspace | null {
  const record = parseRecord(payload)
  if (!record) return null

  const files = readWorkspaceFiles(record)
  const output = getOutput(record)
  const previewUrl = getPreviewUrl(record)

  if (files.length === 0 && !output && !previewUrl) {
    return null
  }

  const source =
    readString(record.source) === 'virtual-files'
      ? 'virtual-files'
      : readString(record.source) === 'code'
        ? 'code'
        : 'computer'

  const title = readString(record.title) ?? 'Workspace'
  const id =
    readString(record.id) ??
    workspaceId(source, readString(record.toolName), files.map((file) => file.path).join('|'))
  const status =
    readString(record.status) === 'running' ||
    readString(record.status) === 'pending' ||
    readString(record.status) === 'completed' ||
    readString(record.status) === 'error'
      ? (readString(record.status) as MiniIdeWorkspaceStatus)
      : 'completed'

  return {
    id,
    title,
    files,
    source,
    status,
    mergeKey: getWorkspaceMergeKey(source, record, readString(record.toolName)),
    output,
    previewUrl,
    selectedPath: readString(record.selectedPath),
    toolName: readString(record.toolName),
    updatedAt: Date.now()
  }
}

export function createMiniIdeWorkspaceFromToolStart(
  toolName: string,
  request: unknown,
  toolId?: string | null
): ActiveMiniIdeWorkspace | null {
  if (!isMiniIdeToolName(toolName)) return null

  const params = unwrapParameters(request)
  if (!params) return null

  if (toolName.startsWith('sandbox__runCode')) {
    const code = readString(params.code)
    if (!code) return null

    const language = readString(params.language) ?? 'javascript'
    const path = codePathForLanguage(language)

    return {
      id: workspaceId('code', toolName, toolId ?? undefined),
      title: 'Code runner',
      source: 'code',
      status: 'running',
      mergeKey: getWorkspaceMergeKey('code', params, toolName),
      files: [
        {
          path,
          name: basename(path),
          content: code,
          language: inferLanguage(path, language),
          status: 'running'
        }
      ],
      output: 'Running code...',
      toolName,
      updatedAt: Date.now()
    }
  }

  const files = readWorkspaceFiles(params).map((file) => ({
    ...file,
    status: 'running' as MiniIdeWorkspaceStatus
  }))
  const previewUrl = getPreviewUrl(params)
  const output = getOutput(params, files.length > 0 ? 'Writing files...' : null)

  if (toolName.startsWith('sandbox__')) {
    if (files.length === 0 && !previewUrl && !output) return null

    return {
      id: workspaceId('computer', toolName, toolId ?? undefined),
      title: previewUrl ? 'Computer preview' : 'Computer workspace',
      source: 'computer',
      status: 'running',
      mergeKey: getWorkspaceMergeKey('computer', params, toolName),
      files,
      output: output || 'Preparing workspace...',
      previewUrl,
      toolName,
      updatedAt: Date.now()
    }
  }

  if (files.length === 0) return null

  return {
    id: workspaceId('virtual-files', toolName, toolId ?? undefined),
    title: 'Files',
    source: 'virtual-files',
    status: 'running',
    mergeKey: getWorkspaceMergeKey('virtual-files', params, toolName),
    files,
    output: 'Writing file...',
    toolName,
    updatedAt: Date.now()
  }
}

export function createMiniIdeWorkspaceFromToolProgress(
  toolName: string,
  progress: unknown,
  toolId?: string | null
): ActiveMiniIdeWorkspace | null {
  if (!isMiniIdeToolName(toolName)) return null

  const rawRecord = parseRecord(progress)
  const record = getNestedRecord(rawRecord, 'progress') ?? rawRecord
  if (!record) return null

  const files = readWorkspaceFiles(record)
  const output = getOutput(record)
  const previewUrl = getPreviewUrl(record)

  if (files.length === 0 && !output && !previewUrl) {
    return null
  }

  const source: MiniIdeWorkspaceSource = toolName.startsWith('sandbox__runCode')
    ? 'code'
    : toolName.startsWith('sandbox__')
      ? 'computer'
      : 'virtual-files'

  return {
    id: workspaceId(source, toolName, toolId ?? undefined),
    title: source === 'code' ? 'Code runner' : source === 'computer' ? 'Computer workspace' : 'Files',
    source,
    status: 'running',
    mergeKey: getWorkspaceMergeKey(source, record, toolName),
    files,
    output,
    previewUrl,
    toolName,
    updatedAt: Date.now()
  }
}

export function createMiniIdeWorkspaceFromToolResult(
  toolName: string,
  result: unknown,
  toolId?: string | null
): ActiveMiniIdeWorkspace | null {
  if (!isMiniIdeToolName(toolName)) return null

  const record = parseRecord(result)
  if (!record) return null

  const files = readWorkspaceFiles(record).map((file) => ({
    ...file,
    status: getStatus(record, 'completed')
  }))
  const output = getOutput(record)
  const previewUrl = getPreviewUrl(record)

  if (files.length === 0 && !output && !previewUrl) {
    return null
  }

  const source: MiniIdeWorkspaceSource = toolName.startsWith('sandbox__runCode')
    ? 'code'
    : toolName.startsWith('sandbox__')
      ? 'computer'
      : 'virtual-files'
  const status = getStatus(record, 'completed')

  return {
    id: workspaceId(source, toolName, toolId ?? undefined),
    title: source === 'code' ? 'Code runner' : source === 'computer' ? 'Computer workspace' : 'Files',
    source,
    status,
    mergeKey: getWorkspaceMergeKey(source, record, toolName),
    files,
    output,
    previewUrl,
    toolName,
    updatedAt: Date.now()
  }
}

export function mergeMiniIdeWorkspaces(
  current: ActiveMiniIdeWorkspace | null,
  next: ActiveMiniIdeWorkspace
): ActiveMiniIdeWorkspace {
  if (!current) return next

  const sameWorkspace =
    current.id === next.id ||
    Boolean(current.mergeKey && current.mergeKey === next.mergeKey) ||
    Boolean(current.toolName && current.toolName === next.toolName)

  if (!sameWorkspace) return next

  return {
    ...current,
    ...next,
    title: next.title || current.title,
    files: uniqueFiles([...current.files, ...next.files]),
    output: next.output || current.output,
    previewUrl: next.previewUrl || current.previewUrl,
    selectedPath: next.selectedPath || current.selectedPath,
    updatedAt: next.updatedAt
  }
}
